/**
 * @module composables/useAssistant
 * @description 운영 어시스턴트의 SSE 스트리밍·JWT 전달·HITL 접수 commit·마크다운 렌더 로직.
 * 기존 AssistantWidget.vue(단일 위젯)에서 추출하여 컴포넌트는 UI만 담당하게 한다.
 * 상태는 Pinia `useAssistantStore`를 통해 공유한다.
 */
import { useAssistantStore } from '@/stores/assistant'
import { loadConversation, taskOfBlock } from '@/stores/assistantStorage'
import { isValidBlock } from '@/composables/blockGuard'
import i18n from '@/locales/i18n'

const API_BASE = process.env.VUE_APP_AI_API_BASE || '/api/ai'
const t = i18n.global.t

// 진행 중 스트리밍의 AbortController(F6 취소 버튼) — 동시 전송은 store.sending 가드로 1건이라 모듈 스코프로 충분
let activeAbort = null
// 진행 중 스트리밍을 시작한 토큰 — BE /chat/cancel 은 토큰 해시로 스트림을 매칭하므로,
// 로그아웃(토큰 제거)·재로그인(새 토큰) 후에도 시작 시점 토큰으로 취소해야 유효하다
let activeToken = null
// 진행 중 스트리밍의 전송 스냅샷(ctx, AA-143) — 응답 중 [등록]·[취소]로 비운 작업을 ctx.cleared 에 남겨
// 그 요청의 done 이 되살리지 않게 한다
let activeCtx = null
// 새로고침·이탈로 문서가 내려가는 중(AA-143) — 이때 끊긴 fetch 를 연결 오류로 저장하지 않는다.
// 전송 시작 때 저장한 빈 placeholder 가 남아야 복원 시 '새로고침으로 중단' 안내로 바뀐다.
// bfcache 로 되살아나면(pageshow) 다시 정상 흐름이다
let pageUnloading = false
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { pageUnloading = true })
  window.addEventListener('pageshow', () => { pageUnloading = false })
}
// 브리핑 프리페치(2026-07-31) — FAB hover 시 네트워크만 선행하고 렌더는 패널 오픈 시.
// {store, epoch, card, comment} 형태의 in-flight Promise 보관. 모듈 스코프라 FAB/패널이 공유한다.
// 재사용 조건에 store 동일성을 포함한다 — epoch 만 비교하면 스토어가 새로 만들어졌을 때
// (재마운트/테스트) 이전 인스턴스의 응답을 그대로 렌더할 수 있다.
let briefingFetch = null

/**
 * 봇 메시지 전용 경량 마크다운 렌더러.
 * 보안: 원본을 먼저 HTML 이스케이프한 뒤 변환하므로 v-html 주입이 안전하다.
 * 예외 안전: 불완전한(스트리밍 중) 마크다운에도 throw 하지 않는다.
 * 지원: 제목(#~###), **굵게**, *이탤릭*, `인라인 코드`, 순서/비순서 목록, GFM 표, 문단/줄바꿈.
 * @param {string} src - 마크다운 원본
 * @returns {string} 안전한 HTML 문자열
 */
export function renderMarkdown(src) {
  try {
    const esc = (s) => String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
    // 인라인: 코드 → 굵게 → 이탤릭 (이미 이스케이프된 문자열 대상)
    const inline = (s) => s
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')

    const lines = esc(src == null ? '' : src).split(/\r?\n/)
    const out = []
    let i = 0
    const isTableRow = (l) => /^\s*\|.*\|\s*$/.test(l)
    const isDivider = (l) => /^\s*\|?[\s:|-]*-+[\s:|-]*\|?\s*$/.test(l) && l.indexOf('-') !== -1

    while (i < lines.length) {
      const line = lines[i]

      // 표: 헤더행 + 구분행 + 본문행
      if (isTableRow(line) && i + 1 < lines.length && isDivider(lines[i + 1])) {
        const cells = (l) => l.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim())
        const head = cells(line)
        i += 2
        let html = '<table><thead><tr>' + head.map((c) => '<th>' + inline(c) + '</th>').join('') + '</tr></thead><tbody>'
        while (i < lines.length && isTableRow(lines[i])) {
          const row = cells(lines[i])
          html += '<tr>' + head.map((_, idx) => '<td>' + inline(row[idx] || '') + '</td>').join('') + '</tr>'
          i++
        }
        html += '</tbody></table>'
        out.push(html)
        continue
      }

      // 제목 (#~###)
      const h = /^(#{1,3})\s+(.*)$/.exec(line)
      if (h) {
        const level = h[1].length
        out.push('<h' + level + '>' + inline(h[2]) + '</h' + level + '>')
        i++
        continue
      }

      // 순서 없는 목록
      if (/^\s*[-*]\s+/.test(line)) {
        const items = []
        while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
          items.push('<li>' + inline(lines[i].replace(/^\s*[-*]\s+/, '')) + '</li>')
          i++
        }
        out.push('<ul>' + items.join('') + '</ul>')
        continue
      }

      // 순서 있는 목록
      if (/^\s*\d+\.\s+/.test(line)) {
        const items = []
        while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
          items.push('<li>' + inline(lines[i].replace(/^\s*\d+\.\s+/, '')) + '</li>')
          i++
        }
        out.push('<ol>' + items.join('') + '</ol>')
        continue
      }

      // 빈 줄 → 문단 구분
      if (line.trim() === '') { i++; continue }

      // 문단: 연속된 일반 텍스트 줄을 <br>로 묶음
      const para = []
      while (
        i < lines.length &&
        lines[i].trim() !== '' &&
        !/^(#{1,3})\s+/.test(lines[i]) &&
        !/^\s*[-*]\s+/.test(lines[i]) &&
        !/^\s*\d+\.\s+/.test(lines[i]) &&
        !(isTableRow(lines[i]) && i + 1 < lines.length && isDivider(lines[i + 1]))
      ) {
        para.push(inline(lines[i]))
        i++
      }
      out.push('<p>' + para.join('<br>') + '</p>')
    }
    return out.join('')
  } catch (e) {
    // 변환 실패 시에도 안전하게 이스케이프된 평문 반환
    return String(src == null ? '' : src)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }
}

/**
 * 어시스턴트 채팅 동작(스트리밍 전송·이벤트 처리·HITL commit)을 제공하는 composable.
 * @returns {{store:object, send:Function, commitDraft:Function, cancelDraft:Function, renderMarkdown:Function}}
 */
export function useAssistant() {
  const store = useAssistantStore()

  /**
   * 초안 카드 수신 즉시 대화 작업을 카드 kind 로 올리고 저장한다(AA-143) — done 이 오지 않는
   * 턴(LLMError·중지)이나 응답 중 새로고침에도 다음 턴이 작업을 이어 간다.
   * @param {string} task - 카드 kind
   * @param {{epoch:number}} ctx - 전송 시점 스냅샷
   */
  function onDraftCard(task, ctx) {
    if (ctx.epoch !== store.sessionEpoch) return
    store.startTask(task)
    store.persist()
  }

  /**
   * 카드 [등록]·[취소]로 대화 작업을 비운다(AA-143). 응답 중이면 비운 kind 를 그 요청 ctx 에 남긴다 —
   * 서버는 이 비움을 모른 채 이번 턴 카드 kind 를 done 으로 돌려준다(applyServerState 가 무시).
   * @param {string|null} kind - 카드 kind
   */
  function clearTask(kind) {
    if (store.clearTask(kind) && activeCtx) activeCtx.cleared.push(kind)
  }

  /**
   * SSE 이벤트 청크 1개를 해석하여 봇 메시지/카드를 갱신한다.
   * @param {string} chunk - 빈 줄로 구분된 SSE 이벤트 블록
   * @param {object} bot - store.pushMessage가 반환한 reactive proxy 봇 메시지
   * @param {{epoch:number, task:string|null}} ctx - 전송 시점 스냅샷(대화 작업 상태 반영용, AA-143)
   */
  function handleEvent(chunk, bot, ctx) {
    let ev = 'message'
    let data = ''
    for (const line of chunk.split(/\r?\n/)) {
      if (line.startsWith('event:')) ev = line.slice(6).trim()
      // SSE 규약: 한 이벤트의 여러 data: 줄은 \n 으로 재조합 (표/목록/문단 줄바꿈 복원)
      else if (line.startsWith('data:')) data += (data ? '\n' : '') + line.slice(5).replace(/^ /, '')
    }
    if (ev === 'token') {
      // block-only 응답의 빈 token(잔여 텍스트 "")은 버블을 시작시키지 않는다 → 빈 버블 방지
      if (!data && !bot._started) return
      if (!bot._started) { bot.text = ''; bot._started = true; bot.statusChip = '' } // 진행 칩 제거 후 실제 답변 시작
      bot.text += data
    } else if (ev === 'status') {
      // 도구 진행 표시는 본문(text)을 오염시키지 않고 칩(statusChip)으로 — AssistantPanel 렌더
      if (!bot._started) { bot.statusChip = data }
    } else if (ev === 'draft') {
      // 하위호환: 기존 draft 이벤트 → draft_confirm 블록으로 통일 라우팅 (AA-46)
      try {
        const parsed = JSON.parse(data)
        store.pushMessage({ kind: 'block', block: { type: 'draft_confirm', data: parsed }, committing: false, done: false, recvAt: Date.now() })
        onDraftCard('repair', ctx)
      } catch (e) { /* 파싱 실패 시 무시(에러 안전) */ }
    } else if (ev === 'block') {
      // 구조화 블록(AA-46): { type, data } JSON. type별 BlockRenderer가 컴포넌트 분기.
      try {
        const parsed = JSON.parse(data)
        // Output Guard L2(AA-48): 구조적으로 깨진 블록은 렌더 파이프라인에서 차단
        if (parsed && parsed.type && isValidBlock(parsed)) {
          // draft_confirm/consultation_draft/admin_draft 은 HITL 상태(committing/done)와 수신 시각(복원 시 만료 안내)을 메시지에 보유
          const task = taskOfBlock(parsed)
          const extra = task ? { committing: false, done: false, recvAt: Date.now() } : {}
          store.pushMessage({ kind: 'block', block: parsed, ...extra })
          if (task) onDraftCard(task, ctx)
        } else if (parsed && parsed.type && window.__APP_LOG__) {
          window.__APP_LOG__('assistant: invalid block payload dropped — type=' + parsed.type)
        }
      } catch (e) { /* 파싱 실패 시 무시(에러 안전) */ }
    } else if (ev === 'error') {
      bot.kind = 'error'
      bot.statusChip = ''
      bot.text = t('assistant.errorPrefix', { msg: data })
    } else if (ev === 'done') {
      // 대화 작업 상태(AA-143) — state 를 보낸 요청에만 {"state":{"task":…}} 가 온다. 빈 data(구 서버·
      // 입력 가드 차단·혼합 노드)·파싱 실패면 현재 상태를 유지한다.
      if (!data) return
      try {
        const parsed = JSON.parse(data)
        if (parsed && parsed.state && typeof parsed.state === 'object') store.applyServerState(parsed.state, ctx)
      } catch (e) { /* 파싱 실패 시 무시(상태 유지) */ }
    }
  }

  /**
   * 입력 텍스트를 ai-service로 전송하고 응답을 스트리밍한다.
   * @param {string} [text] - 보낼 텍스트. 생략 시 store.draft 사용.
   */
  async function send(text) {
    const msg = (text == null ? store.draft : text).trim()
    if (!msg || store.sending) return
    store.draft = ''

    store.pushMessage({ kind: 'user', text: msg })
    const historyBeforeBot = store.chatHistory.slice(0, -1) // 마지막 user 제외하고 직전 이력 전달
    // ⚠️ Vue reactivity: pushMessage가 반환한 배열의 reactive proxy 요소를 참조해야
    //    bot.text 변경이 반응성을 트리거한다.
    const bot = store.pushMessage({ kind: 'bot', text: '' })
    store.sending = true
    // 전송 시점 스냅샷(AA-143) — 세대는 reset 이후 응답 격리, task 는 done 의 서버 '유지' 판별,
    // cleared 는 응답 중 [등록]·[취소]로 비운 작업(clearTask)
    const ctx = { epoch: store.sessionEpoch, task: store.convTask, cleared: [] }
    store.persist()
    const controller = new AbortController()
    activeAbort = controller
    activeCtx = ctx

    try {
      // 운영자 JWT를 ai-service로 전달 → ai-service가 cs-back tool 호출에 forwarding
      const headers = { 'Content-Type': 'application/json' }
      const token = sessionStorage.getItem('authToken')
      if (token) headers['X-CS-Token'] = token
      activeToken = token || null
      const resp = await fetch(`${API_BASE}/chat/stream`, {
        method: 'POST',
        headers,
        signal: controller.signal, // F6 취소 버튼 — abort 시 BE가 disconnect 감지로 tool loop 취소
        // partner_id: ADMIN이 PartnerSelect로 전환한 활성 고객사(commit 타겟). 빈 값=전체.
        // state: 대화 작업 상태(AA-143) — 작업이 없어도 항상 객체로 보낸다(서버는 필드 유무로 상태 경로를 가른다)
        body: JSON.stringify({ message: msg, history: historyBeforeBot, partner_id: store.activePartnerId, state: { task: ctx.task } })
      })
      if (!resp.ok || !resp.body) {
        const t = await resp.text()
        throw new Error(t || `HTTP ${resp.status}`)
      }
      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        // SSE 이벤트 구분자는 빈 줄(\r\n\r\n 또는 \n\n) — CRLF 모두 처리
        const events = buffer.split(/\r?\n\r?\n/)
        buffer = events.pop()
        for (const chunk of events) handleEvent(chunk, bot, ctx)
      }
    } catch (e) {
      if (e && e.name === 'AbortError') {
        // 사용자 취소(F6) — 오류가 아니라 정상 흐름. 대기 버블을 취소 안내로 대체
        bot.statusChip = ''
        if (!bot._started || !bot.text) {
          bot.text = t('assistant.cancelledRequest')
          bot._started = true
        }
      } else if (!pageUnloading) {
        if (window.__APP_LOG__) window.__APP_LOG__(e)
        bot.kind = 'error'
        bot.text = t('assistant.connectionError', { msg: e && e.message ? e.message : e })
      }
    } finally {
      if (activeAbort === controller) {
        activeAbort = null
        activeToken = null
      }
      if (activeCtx === ctx) activeCtx = null
      store.sending = false
      // 문서가 내려가는 중이면 placeholder 정리·저장을 건너뛴다 — 전송 시작 때 저장분이 남는다
      if (!pageUnloading) {
        // block-only 응답(토큰 없이 block 이벤트만) 시 비어 있는 bot placeholder 제거 → 빈 버블 방지
        if (bot.kind === 'bot' && !bot._started && bot.text === '') {
          const idx = store.messages.indexOf(bot)
          if (idx !== -1) store.messages.splice(idx, 1)
        }
        bot.statusChip = '' // 잔여 진행 칩 정리 (도구 실행 후 응답이 error 로 끝난 경우 등)
        if (store.sessionEpoch === ctx.epoch) store.persist()
      }
    }
  }

  /**
   * 응답 생성 중지(F6) — 진행 중 스트리밍 fetch를 abort하고, BE에 명시 취소 신호를 보낸다.
   * dev proxy가 abort를 백엔드로 전파하지 않는 환경이 있어(실관측) /chat/cancel을 병행 호출 —
   * BE가 tool loop task를 즉시 취소하고, 취소된 대화는 세션 메모리/judge 샘플에 기록되지 않는다.
   */
  function cancelSend() {
    if (!activeAbort) return
    const headers = {}
    // 스트림 시작 시점 토큰으로 취소 — 계정 전환/토큰 제거 후에도 BE 해시 매칭이 유효하다
    const token = activeToken || sessionStorage.getItem('authToken')
    if (token) headers['X-CS-Token'] = token
    activeAbort.abort()
    fetch(`${API_BASE}/chat/cancel`, { method: 'POST', headers }).catch(() => { /* fire-and-forget */ })
  }

  /**
   * 브리핑 단건 요청 — 실패/비200은 null 로 흡수(브리핑은 부가 기능, 채팅 비차단).
   * @param {string} path - '/chat/briefing' | '/chat/briefing/comment'
   * @returns {Promise<object|null>}
   */
  function briefingRequest(path) {
    const token = sessionStorage.getItem('authToken')
    if (!token) return Promise.resolve(null)
    return fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CS-Token': token },
      body: JSON.stringify({ partner_id: store.activePartnerId })
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch((e) => {
        if (window.__APP_LOG__) window.__APP_LOG__(e)
        return null
      })
  }

  /**
   * 브리핑 프리페치(2026-07-31) — FAB hover/포커스 시 호출. 네트워크만 미리 시작하고
   * 메시지 렌더는 sendBriefing(패널 오픈)이 담당한다. 카드/문장을 동시에 띄워
   * 문장 LLM(~1.2s)이 카드(~100ms)를 기다리지 않게 한다.
   * 호버만 하고 열지 않아도 BE 쿨다운(10분)이 남발을 막는다.
   */
  function prefetchBriefing() {
    if (store.briefed) return // 이미 렌더 완료된 세션
    if (isFresh(briefingFetch)) return // 이미 진행 중
    if (!sessionStorage.getItem('authToken')) return
    briefingFetch = {
      store,
      epoch: store.sessionEpoch,
      card: briefingRequest('/chat/briefing'),
      comment: briefingRequest('/chat/briefing/comment')
    }
  }

  /** 보관된 in-flight 요청이 현재 스토어·세대의 것인지 */
  function isFresh(f) {
    return !!f && f.store === store && f.epoch === store.sessionEpoch
  }

  /**
   * 프로액티브 브리핑(F1) — 패널 최초 오픈 시 1회, '오늘의 현황'을 선제 브리핑한다.
   * 2026-07-31 2단계: 카드(~70ms)가 먼저 렌더되고, 문장(~1.3s)은 도착 순서 그대로
   * **카드 아래**에 이어 붙는다 — 이미 읽고 있는 카드 위로 내용이 끼어들지 않는다.
   * fire-and-forget: 실패는 조용히 스킵. 쿨다운/스킵 판단은 BE 가 하고 reply 가 비어 온다.
   */
  async function sendBriefing() {
    if (store.briefed) return
    if (!sessionStorage.getItem('authToken')) return
    // 세대 캡처 — 응답 도착 전 reset(로그아웃/계정 전환)이 일어나면 push 하지 않는다
    const epoch = store.sessionEpoch
    // ⚠️ prefetchBriefing 은 store.briefed 를 가드로 쓰므로 반드시 briefed 설정 **전에** 호출한다
    //    (순서를 바꾸면 hover 없이 연 경우 요청이 아예 나가지 않는다). 여기까지 await 없음 → 재진입 안전.
    prefetchBriefing() // 프리페치가 없었으면 지금 시작 (hover 없는 터치 환경)
    store.briefed = true
    const pending = briefingFetch
    if (!isFresh(pending)) return
    try {
      const cardRes = await pending.card
      if (store.sessionEpoch !== epoch) return // 이전 계정의 브리핑 — 새 계정 대화 오염 방지
      for (const b of (cardRes && cardRes.blocks) || []) {
        if (b && b.type && isValidBlock(b)) store.pushMessage({ kind: 'block', block: b })
      }
      // 구 BE(단일 응답) 호환 — reply 가 카드와 함께 오면 카드 뒤에 이어 붙인다
      if (cardRes && cardRes.reply) store.pushMessage({ kind: 'bot', text: cardRes.reply })
      store.persist() // 새로고침 복원(AA-143) — 브리핑은 BE 쿨다운이라 다시 받지 못한다

      const cmtRes = await pending.comment
      if (store.sessionEpoch !== epoch) return
      const text = cmtRes && cmtRes.reply
      if (text) {
        store.pushMessage({ kind: 'bot', text })
        store.persist()
      }
    } catch (e) {
      if (window.__APP_LOG__) window.__APP_LOG__(e)
    }
  }

  /**
   * [등록] 클릭 → /chat/commit 호출. confirmation_token은 일회성이므로
   * 성공/실패 모두 카드를 done 처리하여 중복 커밋을 막는다.
   * ⚠️ 알려진 한계(의도적, MVP 계승): 응답 유실/일시 장애 시에도 done 처리하여
   *   재시도를 막는다. 서버 commit 여부 불명 상태에서 재시도 시 중복 접수가 발생할
   *   수 있어 보수적으로 닫는 선택이다. 재시도 허용은 BE 멱등 commit 키 계약 필요(Out of Scope).
   * @param {object} m - draft 카드 메시지
   */
  async function commitDraft(m, selections) {
    if (m.committing || m.done) return
    // draft 데이터는 draft_confirm 블록의 data (하위호환: 구 m.draft 도 허용)
    const d = (m.block && m.block.data) || m.draft || {}
    m.committing = true
    // 세대 캡처 — commit 응답 도착 전 reset(로그아웃/계정 전환) 시 결과 메시지 push 방지
    const epoch = store.sessionEpoch
    store.persist() // 등록 중 새로고침되면 복원 시 결과 불명(commitUnknown)으로 닫는다(AA-143)
    try {
      const body = {
        draft_id: d.draft_id,
        confirmation_token: d.confirmation_token,
        // partner_id: 활성 고객사를 commit 타겟으로 명시 전달 (BE 권한·정합성 검증 책임)
        partner_id: store.activePartnerId
      }
      // 후보 칩 로컬 선택({product_id, main_type_code, user_group_id, sub_type_codes}) —
      // LLM 재경유 없이 commit 시 구조화 반영 (2026-07-29, BE 가 draft.allowed_selections
      // 화이트리스트로 검증 후 command 치환)
      if (selections && typeof selections === 'object') {
        Object.assign(body, selections)
      }
      const resp = await fetch(`${API_BASE}/chat/commit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CS-Token': sessionStorage.getItem('authToken') || ''
        },
        body: JSON.stringify(body)
      })
      const result = await resp.json()
      // reset(계정 전환) 이후 도착한 응답 — 카드 상태만 닫고 새 대화에 메시지를 남기지 않는다
      const stale = store.sessionEpoch !== epoch
      if (result && result.status === 'ok') {
        m.done = true
        m.committing = false
        if (stale) return
        // 대화 작업 비움(AA-143) — 접수·상담은 등록으로 작업이 끝난다. 관리는 연속 등록 체인이라 유지하고,
        // 실패는 다시 요청할 수 있게 유지한다
        const task = taskOfBlock(m.block)
        if (task !== 'admin') clearTask(task)
        if (result.kind === 'consultation') {
          // 상담 초안 commit(AA-117) — kind 분기 응답, link 블록 entity='consultation'(navRegistry 기등록)
          // ⚠️ BE 쓰기 흐름 경계 마커 계약(AA-128): assistant.consultationRegisterSuccess/registerSuccess/cancelled 문구(ko/en/jp)를
          //   cs-ai-agent app/core/intent_flow.py 가 정규식으로 읽어 흐름을 종료한다 — 문구 변경 시 BE 정규식·tests/test_intent_flow.py 동기화
          store.pushMessage({ kind: 'bot', text: t('assistant.consultationRegisterSuccess', { id: result.consultation_id }) })
          if (result.consultation_id) {
            store.pushMessage({
              kind: 'block',
              block: { type: 'link', data: { entity: 'consultation', id: result.consultation_id } }
            })
          }
          // AA-144 — 기존 고객(FOUND)이면 cs-back 이 입력 이메일을 저장하지 않는다. 완료 문구는 끝이 번호여야 하는
          //   흐름 경계 마커라 이어 붙이지 않고 별도 메시지로 알린다(필드가 없는 구 ai-service 응답이면 안내 없음).
          if (result.email_ignored === true) {
            store.pushMessage({ kind: 'bot', text: t('assistant.consultationEmailNotSaved') })
          }
        } else if (result.kind === 'admin') {
          // 관리 설정 초안 commit(AA-119, D10) — 성공 메시지만. 자동배정 등은 화면 매핑이
          // 없어 link 블록은 생략한다(navRegistry에 재사용 가능 entity가 있을 때만 추가).
          // 서버 message 우선 — 인프라 등록(AA-120)은 실제 등록/스킵 건수를 담아 내려준다.
          store.pushMessage({ kind: 'bot', text: result.message || t('assistant.adminCommitDone') })
        } else {
          store.pushMessage({ kind: 'bot', text: t('assistant.registerSuccess', { id: result.repair_request_id }) })
          // 등록 건 상세 이동 링크 — 기존 link 블록/navRegistry('repair') 재사용, partnerId 누락 시 목록 폴백
          if (result.repair_request_id) {
            store.pushMessage({
              kind: 'block',
              block: {
                type: 'link',
                data: { entity: 'repair', id: result.repair_request_id, params: { partnerId: result.partner_id } }
              }
            })
          }
        }
      } else {
        m.done = true
        m.committing = false
        if (stale) return
        store.pushMessage({ kind: 'error', text: t('assistant.registerFail', { msg: (result && result.message) || t('assistant.unknownError') }) })
      }
    } catch (e) {
      if (window.__APP_LOG__) window.__APP_LOG__(e)
      m.done = true
      m.committing = false
      if (store.sessionEpoch !== epoch) return
      store.pushMessage({ kind: 'error', text: t('assistant.registerFail', { msg: e && e.message ? e.message : t('assistant.networkError') }) })
    } finally {
      if (store.sessionEpoch === epoch) store.persist()
    }
  }

  /**
   * [취소] 클릭 → 카드를 done 처리하고 안내 메시지 추가.
   * @param {object} m - draft 카드 메시지
   */
  function cancelDraft(m) {
    if (m.committing || m.done) return
    m.done = true
    clearTask(taskOfBlock(m.block)) // 카드 [취소]는 관리 카드도 작업을 비운다(AA-143)
    // BE 쓰기 흐름 경계 마커(AA-128) — assistant.cancelled 문구 변경 시 cs-ai-agent intent_flow.py 동기화
    store.pushMessage({ kind: 'bot', text: t('assistant.cancelled') })
    store.persist()
  }

  /**
   * 새로고침 대화 복원(AA-143) — 위젯 마운트 시 호출. 이미 대화가 있으면(재마운트) 무시한다.
   * 인사말·브리핑 플래그를 함께 되살려 패널을 다시 열어도 중복으로 띄우지 않는다.
   * @returns {boolean} 복원 여부
   */
  function restoreConversation() {
    if (store.messages.length || store.greeted) return false
    const snap = loadConversation()
    if (!snap) return false
    for (const m of snap.messages) store.pushMessage(m.noteKey ? { kind: 'error', text: t(m.noteKey) } : m)
    store.greeted = snap.greeted || store.messages.length > 0
    store.briefed = snap.briefed
    store.convTask = snap.task
    return true
  }

  return {
    store,
    send,
    cancelSend,
    sendBriefing,
    prefetchBriefing,
    commitDraft,
    cancelDraft,
    restoreConversation,
    renderMarkdown
  }
}
